/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    ErrorType, ModelResponse, navigate, ResponseData, showDialog, showSnackbar, validateEmail
} from '@dvrd/dvr-controls';
import EmailTemplate, {EmailTemplateFields} from '../../../../models/emailTemplateModel';
import {createErrorMessage} from '../../../../util/utils';
import {trackPlatformAction} from '../../../../util/analytics';
import TemplateEditor from './templateEditor';
import TestMailController, {TestMailRef} from '../../test/testMailController';
import EmailTemplatePreview from '../preview/emailTemplatePreview';
import EmailStep from '../../../../models/emailStepModel';
import {clearNavigationParam, getNavigationParam} from '../../../../util/navigateUtil';
import {ActivateHandle, EmailTip, UnlayerDesign} from '../../../../util/interfaces';
import PageChangePermission from '../../../misc/pageChangePermission';
import {PermissionCallback} from '../../../../util/pageChanger';
import {EMAILS_ROUTE} from '../../../../util/constants';

export enum EditorType {LEGACY, HTML}

const logoPlaceholder = 'https://assets.unlayer.com/projects/257296/1731930814760-bolmate.png'; // Bolmate logo

const knownPlaceholders: Array<string> = [
    'https://assets.unlayer.com/projects/257296/1733750854444-Screenshot%20from%202024-12-05%2010-56-46.png', // Team img
    'https://assets.unlayer.com/projects/257296/1733392863955-Screenshot%20from%202024-12-05%2010-56-46.png', // Team img
    'https://assets.unlayer.com/projects/257296/1733826756759-Screenshot%20from%202024-12-05%2010-56-46.png', // Team img
    'https://assets.unlayer.com/projects/257296/1733826463055-Screenshot%20from%202024-12-05%2010-56-46-modified.png' // Team img (small)
];

function validateContent(htmlContent: string): Array<EmailTip> {
    const contentWarnings: Array<EmailTip> = [];
    if (htmlContent.includes('{bedrijf_email}')) contentWarnings.push(EmailTip.COMPANY_EMAIL);
    if (htmlContent.includes('{product_url}') && !hasHonestText(htmlContent)) contentWarnings.push(
        EmailTip.HONEST_REVIEW);
    if (htmlContent.includes('⭐')) contentWarnings.push(EmailTip.GOLDEN_STAR);
    return contentWarnings;
}

function hasHonestText(htmlContent: string) {
    const reg = /eerlijk|honnête|sincère/i;
    return reg.test(htmlContent);
}

export default function TemplateEditorController() {
    const [loading, setLoading] = useState(false);
    const [template, setTemplate] = useState<EmailTemplate | null>(null);
    const [bccError, setBccError] = useState<ErrorType>(null);
    const [editorType, setEditorType] = useState<EditorType>(EditorType.HTML);
    const [checkedTips, setCheckedTips] = useState<Array<EmailTip>>([]);
    const [contentWarnings, setContentWarnings] = useState<Array<EmailTip>>([]);
    const showTips: Array<EmailTip> = useMemo(() => {
        const tips: Array<EmailTip> = [];
        const body = template?.json;
        if (!body) return tips;
        const stringBody = JSON.stringify(body);
        if (stringBody.includes(logoPlaceholder)) tips.push(EmailTip.BOLMATE_LOGO);
        if (!!knownPlaceholders.find((placeholder: string) => stringBody.includes(placeholder)))
            tips.push(EmailTip.STOCK_PHOTO);
        return tips;
    }, [template?.id]);
    const testMailRef = useRef<TestMailRef>(null);
    const previewRef = useRef<ActivateHandle>(null);
    const emailStep = useRef<EmailStep | null>(null);
    const returnRoute = useRef<string>(EMAILS_ROUTE);

    function _setTemplate(template: EmailTemplate) {
        const editorType: EditorType = template.hasJson() ? EditorType.HTML : EditorType.LEGACY;
        setEditorType(editorType);
        setTemplate(template);
        setContentWarnings(validateContent(template.htmlBody));
    }

    function onCheckTip(tip: EmailTip) {
        return function () {
            const checked = checkedTips.slice();
            if (checked.includes(tip)) return;
            checked.push(tip);
            setCheckedTips(checked);
        };
    }

    function onResetError() {
        setBccError(null);
    }

    function onOpenTestAddress() {
        testMailRef.current?.activate();
    }

    function onCloseTestAddress() {
        testMailRef.current?.onClose();
    }

    function onOpenPreview() {
        if (template)
            previewRef.current?.activate(template.body, false, false, template.bcc, template.subject);
    }

    function onCancel() {
        navigate(returnRoute.current ?? '/emails');
    }

    function onChange(key: keyof EmailTemplateFields) {
        return function (value: string) {
            if (!template) return;
            if (key === 'bcc') onResetError();
            const tpl = template.clone();
            tpl.setField(key, value);
            setTemplate(tpl);
        };
    }

    function onChangeHTMLEditor(data: { design: UnlayerDesign, html: string },
                                callback?: (template?: EmailTemplate) => void) {
        if (!template) return;
        const contentWarnings = validateContent(data.html);
        if (contentWarnings.length) return setContentWarnings(contentWarnings);
        const tpl = template.clone();
        tpl.json = data.design;
        tpl.htmlBody = data.html;
        setTemplate(tpl);
        if (callback)
            window.setTimeout(() => {
                callback(tpl);
            });
    }

    function onClearTemplate() {
        onChange('body')('');
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        onSubmit(undefined, () => {
            callback(id, true);
        });
    }

    function onSubmit(_template?: EmailTemplate, callback?: VoidFunction) {
        if (!template && !_template) return;
        _template = (_template ?? template)!;
        if (!validateValues(_template)) return;
        setLoading(true);
        if (_template.id) onUpdate(_template, callback);
        else onSave(_template, callback);
    }

    function onSave(template: EmailTemplate, callback?: VoidFunction) {
        template.save((template: EmailTemplate, success: boolean, data: ResponseData) => {
            if (success) {
                onUpdateStep(template, (step: EmailStep, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        trackPlatformAction('emails.templates', 'save_template');
                        showSnackbar('De e-mail template is opgeslagen');
                        if (callback) callback();
                        else onCancel();
                    } else showDialog(
                        createErrorMessage(data.message ?? 'Het opslaan van de e-mail template is niet gelukt.'),
                        'Opslaan mislukt');
                });
            } else {
                setLoading(false);
                showDialog(createErrorMessage(data.message ?? 'Het opslaan van de e-mail template is niet gelukt.'),
                    'Opslaan mislukt');
            }
        });
    }

    function onUpdate(template: EmailTemplate, callback?: VoidFunction) {
        template.update((_: EmailTemplate, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                trackPlatformAction('emails.templates', 'save_template');
                showSnackbar('De e-mail template is bijgewerkt');
                if (callback) callback();
                else onCancel();
            } else
                showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mail template is niet gelukt.'),
                    'Bijwerken mislukt');
        });
    }

    function onUpdateStep(template: EmailTemplate, callback: ModelResponse<EmailStep>) {
        const step = emailStep.current;
        if (!step) return;
        step.templateId = template.id;
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            if (callback) callback(step, success, data);
        });
    }

    function onSendTestMail(recipient: string) {
        if (!template || !emailStep.current) return;
        testMailRef.current?.setLoading(true);
        template.test(recipient, emailStep.current.id,
            (_: EmailTemplate, success: boolean, data: ResponseData) => {
                testMailRef.current?.setLoading(false);
                if (success) {
                    onCloseTestAddress();
                    showSnackbar('De testmail is verzonden');
                } else showDialog(
                    createErrorMessage(data.message ?? 'Het verzenden van de testmail is niet gelukt.'),
                    'Verzenden mislukt');
            });
    }

    function validateValues(template: EmailTemplate): boolean {
        if (!template) return false;
        if (!validatePlaceholders(template)) return false;
        let valid: boolean = true;
        if (template.bcc && !validateEmail(template.bcc, true)) {
            valid = false;
            setBccError('Het BCC adres is niet geldig');
        }
        return valid;
    }

    function validatePlaceholders(template: EmailTemplate): boolean {
        if (template?.htmlBody.includes(logoPlaceholder)) {
            showDialog(
                'De e-mail bevat nog placeholder afbeeldingen van het logo van Bolmate. Vervang deze met jouw eigen logo of verwijder de afbeelding.');
            return false;
        }
        for (const placeholder of knownPlaceholders) {
            if (template?.htmlBody.includes(placeholder)) {
                showDialog(
                    'De e-mail bevat nog placeholder afbeeldingen van personen. Vervang deze met jouw eigen foto of verwijder de afbeelding.');
                return false;
            }
        }
        return true;
    }

    useEffect(() => {
        const navParam = getNavigationParam();
        clearNavigationParam();
        if (!navParam) {
            navigate(EMAILS_ROUTE);
            return;
        }
        const {template, step, returnPage} = navParam;
        if (!template || !step) {
            if (returnPage) navigate(returnPage);
            else navigate('/emails');
            return;
        }
        returnRoute.current = returnPage;
        emailStep.current = step;
        _setTemplate(template);
    }, []);

    return (
        <>
            <EmailTemplatePreview ref={previewRef}/>
            <TestMailController onSubmit={onSendTestMail} ref={testMailRef} closeOnSubmit={false}/>
            <TemplateEditor onClickPreview={onOpenPreview} onCancel={onCancel} onChange={onChange}
                            onChangeHTMLEditor={onChangeHTMLEditor} onClearTemplate={onClearTemplate}
                            onClickTest={onOpenTestAddress} onSubmit={onSubmit} onCheckTip={onCheckTip}
                            onResetError={onResetError} loading={loading} template={template} bccError={bccError}
                            editorType={editorType} showTips={showTips} checkedTips={checkedTips}
                            contentWarnings={contentWarnings}/>
            <PageChangePermission changed={true} onDeny={onDenyPageChange}
                                  denyText='Ja, wijzigingen opslaan' allowText='Nee, pagina verlaten'
                                  allowSecondary={true}>
                De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?
            </PageChangePermission>
        </>
    );
}